import { Link } from "react-router-dom";
import { Sparkles, Heart, ShieldCheck, Users } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";

// FILL THIS IN WHEN READY. While "name" is empty, the author box is hidden.
const AUTHOR = {
  name: "",        // e.g. "Rahul Sharma"
  role: "",        // e.g. "Founder and numerology writer"
  bio: "",         // 2 to 4 true lines about your background
  nameSystem: "",  // "Chaldean" or "Pythagorean" (leave empty if not sure)
};

const VALUES = [
  { icon: Heart, title: "Warmth first", text: "Every reading is written to encourage, never to frighten. We believe guidance should feel like a friend, not a fortune-teller." },
  { icon: ShieldCheck, title: "Honesty always", text: "We never make guaranteed claims. Numerology is a lens for reflection, and we're clear about that." },
  { icon: Sparkles, title: "Craft & beauty", text: "From typography to tools, we sweat the details so your experience feels calm and premium." },
  { icon: Users, title: "For everyone", text: "Whether you're curious or devoted, Numoraa meets you with clarity and respect." },
];

export default function About() {
  return (
    <div className="pt-28 md:pt-32">
      <SEO title="About Numoraa" description="Numoraa blends ancient numerology with modern AI to offer warm, honest, beautiful guidance. Learn about our mission and values." path="/about" />
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]} />
        <Reveal>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter">We read numbers, <span className="italic text-primary">gently</span>.</h1>
          <p className="mt-8 text-xl font-light text-muted-foreground leading-relaxed">
            Numoraa was born from a simple belief: that the ancient wisdom of numerology deserves a modern, honest and beautiful home. We pair time-honoured traditions with thoughtful AI to help you understand yourself a little better — without fear, pressure or empty promises.
          </p>
          <p className="mt-6 text-lg font-light text-muted-foreground leading-relaxed">
            Our tools are free, our guidance is warm, and our AI Pandits are here whenever you'd like to talk. Everything we build is designed to feel calm, premium and genuinely useful.
          </p>
        </Reveal>

        {AUTHOR.name && (
          <Reveal>
            <div className="mt-16 rounded-3xl border border-border bg-card p-8 shadow-soft" data-testid="author-box">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">Written by</p>
              <h2 className="mt-3 font-serif text-3xl">{AUTHOR.name}</h2>
              {AUTHOR.role && <p className="mt-1 text-sm text-muted-foreground">{AUTHOR.role}</p>}
              {AUTHOR.bio && <p className="mt-4 text-muted-foreground leading-relaxed">{AUTHOR.bio}</p>}
            </div>
          </Reveal>
        )}

        <Reveal>
          <div className="mt-16" data-testid="about-method">
            <h2 className="font-serif text-3xl md:text-4xl">How our readings work</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Our tools calculate your Mulank from your birth day, your Bhagyank from your full date of birth, and your name number from your name, using traditional Indian numerology. AI then helps write the explanations, so each reading is easy to read and personal to your numbers.
            </p>
            {AUTHOR.nameSystem && (
              <p className="mt-4 text-muted-foreground leading-relaxed">
                For name numbers we use the {AUTHOR.nameSystem} system.
              </p>
            )}
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Numerology is a tool for reflection, not a science, and our readings are not predictions or guarantees. See exactly how the numbers are worked out on our{" "}
              <Link to="/tools/numerology-calculator" className="text-primary underline">numerology calculator</Link>, or read{" "}
              <Link to="/blog/mulank-vs-bhagyank-what-is-the-difference" className="text-primary underline">Mulank vs Bhagyank</Link>.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Questions or feedback? <Link to="/contact" className="text-primary underline">Contact us</Link>. Please also read our{" "}
              <Link to="/disclaimer" className="text-primary underline">Disclaimer</Link>.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 pb-24">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-border bg-card p-7 shadow-soft">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary"><v.icon className="h-5 w-5 text-primary" strokeWidth={1.6} /></span>
                <h2 className="mt-5 font-serif text-2xl">{v.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mb-24 rounded-[2rem] bg-primary text-primary-foreground p-10 text-center">
            <h2 className="font-serif text-3xl md:text-4xl">Ready to meet your numbers?</h2>
            <Link to="/tools/numerology-calculator" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-7 py-3.5 transition-colors hover:bg-white/20">Get your free reading →</Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}